import { useMemo } from 'react';
import {
  CanvasGroup,
  CanvasNode,
  CanvasEdge,
  CanvasElementType,
} from '../../sweep-canvas/canvas-types';
import { TRANSIENT_GROUP_ID } from '../useFunnelMapElements';
import { TransientFunnel } from '../funnelMapCanvasTypes';
import { SweepCanvasReactFlowEdgeDataType } from '../../sweep-canvas/edges/SweepCanvasReactFlowEdgeDataType';
import { FunnelType } from '../../../types/enums/FunnelType';

export const TPF_OBJECT_TYPE = '__third_party_funnel__';

export const useCalculateThirdPartyNodes = ({
  funnelMap,
  transientFunnel,
  editingThirdPartyStepId,
  stagesWithSparkleIcon,
}: {
  funnelMap: FunnelMap;
  transientFunnel?: TransientFunnel;
  editingThirdPartyStepId?: string;
  stagesWithSparkleIcon?: string[];
}) => {
  return useMemo(() => {
    const groups: CanvasGroup[] = [];
    const nodes: CanvasNode[] = [];
    const edges: CanvasEdge[] = [];

    const sanitizedThirdPartyFunnelsEntries = Object.entries(
      funnelMap.thirdPartyFunnels || {},
    ).filter(([id]) => funnelMap.thirdPartyFunnelsData[id]);

    sanitizedThirdPartyFunnelsEntries.forEach(([id, { position, sectionId }]) => {
      const { funnelLinks, name, steps, description, system } = funnelMap.thirdPartyFunnelsData[id];

      /// calculates groups
      groups.push({
        nodeType: 'group',
        id,
        name,
        objectLabel: system?.name || '3rd party integration',
        objectLabelLogoDevBrand: system,
        objectType: TPF_OBJECT_TYPE,
        position,
        metadata: {
          funnelLinks,
          steps,
          description,
          system,
        },
        plugins: [],
        description,
        sectionId,
      });

      // calculates nodes
      if (steps.length === 0) {
        const ghostNodeId = `${id}-ghost-node0`;

        if (editingThirdPartyStepId !== ghostNodeId) {
          nodes.push({
            nodeType: 'node',
            id: `${id}-ghost-node0`,
            name: 'Add integration steps',
            type: CanvasElementType.GHOST_NODE,
            objectType: TPF_OBJECT_TYPE,
            position: { column: 0, row: 0 },
            parentId: id,
          });
        }
      } else {
        steps.forEach((step) => {
          nodes.push({
            nodeType: 'node',
            id: step.id,
            name: step.name,
            type: CanvasElementType.REGULAR,
            objectType: TPF_OBJECT_TYPE,
            position: step.position,
            parentId: id,
            hideNbButton: true,
            showSparkleIcon: stagesWithSparkleIcon?.includes(step.id),
            allowNodeSwap: true,
          });
          step.stepConnections.forEach((connection) => {
            edges.push({
              id: connection._id,
              source: step.id,
              target: connection.targetId,
              data: {
                type: SweepCanvasReactFlowEdgeDataType.REMOVABLE,
              },
            });
          });

          step.funnelLinks?.forEach((link) => {
            if (link._id) {
              const targetPrefix = link._type === FunnelType.METADATA_ELEMENT ? '' : 'label-';
              const target = `${targetPrefix}${link.funnelId}`;
              edges.push({
                id: link._id,
                source: step.id,
                target: link.stageId || target,
                data: {
                  type: SweepCanvasReactFlowEdgeDataType.REMOVABLE,
                  groupConnection: true,
                },
              });
            }
          });
        });
      }
      funnelLinks.forEach((link) => {
        if (link.stageId) {
          edges.push({
            id: link._id || `link-${id}-${link.stageId}`,
            source: `label-${id}`,
            target: link.stageId,
            data: {
              type: SweepCanvasReactFlowEdgeDataType.REMOVABLE,
              groupConnection: true,
            },
          });
        } else {
          const targetPrefix = link._type === FunnelType.METADATA_ELEMENT ? '' : 'label-';
          const target = `${targetPrefix}${link.funnelId}`;
          edges.push({
            id: link._id || `link-${id}-${link.funnelId}`,
            source: `label-${id}`,
            target,
            data: {
              type: SweepCanvasReactFlowEdgeDataType.REMOVABLE,
              groupConnection: true,
            },
          });
        }
      });
    });

    if (
      transientFunnel?.type === FunnelType.THIRD_PARTY &&
      transientFunnel?.importType === 'create'
    ) {
      const {
        name,
        position,
        data: { system },
        sectionId,
      } = transientFunnel;
      groups.push({
        nodeType: 'group',
        id: TRANSIENT_GROUP_ID,
        name,
        position,
        objectType: TPF_OBJECT_TYPE,
        objectLabel: system?.name || '',
        objectLabelLogoDevBrand: system,
        plugins: [],
        sectionId,
      });
      nodes.push({
        nodeType: 'node',
        id: `${TRANSIENT_GROUP_ID}-ghost-node0`,
        name: 'Add integration steps',
        type: CanvasElementType.GHOST_NODE,
        objectType: TPF_OBJECT_TYPE,
        position: { column: 0, row: 0 },
        parentId: TRANSIENT_GROUP_ID,
      });
    }

    return { groups, nodes, edges };
  }, [
    funnelMap.thirdPartyFunnels,
    funnelMap.thirdPartyFunnelsData,
    transientFunnel,
    editingThirdPartyStepId,
    stagesWithSparkleIcon,
  ]);
};
